/* ============================================
   Ficha de un contacto — quién es y de qué empresas

   Se monta en el ERP (`/clientes/contactos/:id`) y en el espacio del partner
   (`/partner/contactos/:id`), con la misma pantalla: lo que cambia es el
   prefijo de las rutas, porque las fichas viven en sitios distintos.

   Lee `/api/contactos/:id/empresas`, que valida el acceso por rol. El camino
   anterior era `supabase.from('contacto_empresa')` directo desde el
   componente, prohibido por AGENTS.md y además sin control de alcance.
   ============================================ */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { slugRol } from '@/lib/utils'
import type { Contacto, ContactoEmpresa, Cliente } from '@/lib/types'
import { PageHeader } from '@/components/ui'
import { IconEdit, IconUser, IconBuilding, IconMail, IconPhone, IconArrowRight } from '@/components/icons/Icons'

type AfilConNombre = ContactoEmpresa & { empresa_nombre: string }

export default function VerContacto() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const api = useApi()
  const enPartner = useLocation().pathname.startsWith('/partner')
  const rutaContactos = enPartner ? '/partner/contactos' : '/clientes/contactos'
  const rutaEmpresas = enPartner ? '/partner/empresas' : '/clientes'

  const { data: contactos = [], isLoading } = useCachedQuery<Contacto>('contactos')
  const { data: clientes = [] } = useCachedQuery<Cliente>('clientes')
  const [afiliaciones, setAfiliaciones] = useState<AfilConNombre[]>([])

  const contacto = contactos.find(c => c.id === Number(id))

  useEffect(() => {
    if (!id || clientes.length === 0) return
    api.get<ContactoEmpresa[]>(`/api/contactos/${id}/empresas`)
      .then(filas => setAfiliaciones(filas
        // Una empresa sin nombre es una que este usuario no ve: se omite en
        // vez de dibujarse como un guion que no lleva a ninguna parte.
        .filter(ce => clientes.some(c => c.id === ce.cliente_id))
        .map(ce => ({ ...ce, empresa_nombre: clientes.find(c => c.id === ce.cliente_id)!.empresa }))
        .sort((a, b) => Number(b.es_principal) - Number(a.es_principal))))
      .catch(() => setAfiliaciones([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, clientes])

  if (isLoading || !contacto) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={[{ label: 'Contactos', to: rutaContactos }, { label: 'Contacto' }]} />
      <div className="empty-state">
        {isLoading
          ? <><div className="auth-spinner" />Cargando...</>
          : <><h3>Contacto no encontrado</h3><p>Puede que lo hayan eliminado o que no sea tuyo.</p></>}
      </div>
    </div>
  )

  const nombreCompleto = `${contacto.nombre} ${contacto.apellido ?? ''}`.trim()

  return (
    <div className="page-enter ficha">
      <PageHeader
        breadcrumbs={[{ label: 'Contactos', to: rutaContactos }, { label: nombreCompleto }]}
        meta={contacto.cargo || undefined}
        actions={
          <button className="btn btn-primary" onClick={() => navigate(`${rutaContactos}?edit=${contacto.id}`)} id="btn-edit-contacto">
            <IconEdit size={16} /> Editar
          </button>
        }
      />

      <div className="card ficha__bloque">
        <div className="ficha__titulo"><IconUser size={15} /> Datos del contacto</div>
        <dl className="ficha__datos">
          <div><dt>Nombre</dt><dd>{nombreCompleto}</dd></div>
          <div>
            <dt><IconMail size={12} /> Email</dt>
            <dd>{contacto.email ? <a href={`mailto:${contacto.email}`} className="cell-enlace">{contacto.email}</a> : '\u2014'}</dd>
          </div>
          <div>
            <dt><IconPhone size={12} /> Teléfono</dt>
            <dd>{contacto.telefono ? <a href={`tel:${contacto.telefono}`} className="cell-enlace">{contacto.telefono}</a> : '\u2014'}</dd>
          </div>
          <div><dt>Cargo</dt><dd>{contacto.cargo || '\u2014'}</dd></div>
        </dl>
        {contacto.notas && <p className="ficha__notas">{contacto.notas}</p>}
      </div>

      <div className="card ficha__bloque">
        <div className="ficha__titulo">
          <IconBuilding size={15} /> Empresas
          <span className="ficha__contador">{afiliaciones.length}</span>
        </div>
        {afiliaciones.length === 0 ? (
          <p className="ficha__vacio">Todavía no está vinculado a ninguna empresa.</p>
        ) : (
          <ul className="ficha__lista">
            {afiliaciones.map(af => (
              <li key={af.cliente_id}>
                <Link to={`${rutaEmpresas}/${af.cliente_id}`} className="ficha__item">
                  <span className="ficha__item-texto">
                    <span className="ficha__item-label">
                      {af.empresa_nombre}
                      {af.es_principal && <span className="rol-chip rol-chip--principal">Principal</span>}
                    </span>
                    {af.cargo_empresa && <span className="ficha__item-sub">{af.cargo_empresa}</span>}
                    {af.roles.length > 0 && (
                      <span className="ficha__item-roles">
                        {af.roles.map(r => <span key={r} className={`rol-chip rol-chip--${slugRol(r)}`}>{r}</span>)}
                      </span>
                    )}
                  </span>
                  <IconArrowRight size={15} className="ficha__item-flecha" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
